<script lang="ts">
	import { Theme } from '@tools';
	import type { ThemeKey } from '@tools/styles/ColorThemes.svelte';
	import type { Snippet } from 'svelte';
	import type { HTMLFormAttributes } from 'svelte/elements';

	let {
		children,
		target,
		method = 'POST',
		theme,
		loading = $bindable(false),
		status = $bindable(undefined),
		values = $bindable({}),
		...rest
	}: {
		children: Snippet;
		target?: string;
		method?: string;
		loading?: boolean;
		status?: number | undefined;
		theme?: ThemeKey;
		values?: Record<string, unknown>;
	} & Omit<HTMLFormAttributes, 'method'> = $props();

	const handleSubmit = async (e: SubmitEvent) => {
		const element = e.target as HTMLFormElement | null;
		if (!target || loading) return;

		const body = JSON.stringify(
			Object.fromEntries(
				Object.entries(values)
					.map(([key, value]) => {
						if (!value) {
							return [key, null];
						}
						if ((value as Array<unknown>).length === 0) {
							return [key, null];
						}
						return [key, value];
					})
					.filter(([_, value]) => value !== null)
			)
		);

		e.preventDefault();

		if (body) {
			status = undefined;
			loading = true;

			fetch(target, {
				method,
				body
			})
				.then((res) => {
					status = res.status;
					if (res.status >= 200 && res.status < 400) element?.reset();
				})
				.catch((error) => {
					status = 500;
					console.error(error);
				})
				.finally(() => {
					loading = false;
				});
		}
	};

	const formTheme = new Theme(theme);
</script>

<form
	class="block:form block:large"
	data-size="max-width"
	onsubmit={handleSubmit}
	style={formTheme.css}
	{...rest}
>
	{@render children()}
</form>

<style lang="scss">
	form {
		display: flex;
		flex-direction: column;
		align-items: start;
		background-color: var(--theme-primary);
		color: var(--theme-foreground);
		padding: $gap6 $gap5;
		gap: $gap5;

		@include maxWidthBlock;

		@include md {
			padding: $gap9 cols(12, 1, false, true);
			padding-right: cols(12, 5, false, true);
		}
	}
</style>
